import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, X, PackageSearch, CheckSquare, Square, ChevronRight, ScrollText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BKP_JKP_DATA } from "@/data/bkpJkpData";

const KET_OPTIONS = ["BKP Tertentu", "JKP Tertentu", "BKP Strategis", "JKP Strategis", "BKP bebas BM"];
const FAS_OPTIONS = ["Dibebaskan", "Tidak Dipungut"];
const FAS_STYLE = {
  Dibebaskan: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  "Tidak Dipungut": "bg-sky-50 text-sky-700 ring-sky-200",
};

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
const slug = (s) => norm(s).replace(/ /g, "-");
const INDEX = BKP_JKP_DATA.map((d) => ({ ...d, _n: ` ${norm(d.nama)} ` }));

function scoreItem(item, tokens) {
  let score = 0;
  for (const t of tokens) {
    if (!item._n.includes(t)) return -1;
    score += item._n.includes(` ${t} `) ? 3 : item._n.includes(` ${t}`) ? 2 : 1;
  }
  return score;
}

function Chip({ id, active, onClick, children }) {
  return (
    <button
      data-testid={id}
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-colors ${
        active ? "bg-[#0a3d62] text-white ring-[#0a3d62]" : "bg-white text-slate-600 ring-slate-200 hover:ring-[#0a3d62]/40 hover:text-[#0a3d62]"
      }`}
    >
      {children}
    </button>
  );
}

function Highlight({ text, tokens }) {
  if (!tokens.length) return text;
  const re = new RegExp(`(${tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return text.split(re).map((part, i) =>
    i % 2 ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-slate-900">{part}</mark> : part
  );
}

function Check({ on, label }) {
  const Icon = on ? CheckSquare : Square;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${on ? "text-[#0a3d62]" : "text-slate-400"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
}

function ItemCard({ item, tokens, onOpen, index }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index, 12) * 0.02 }}
      data-testid={`bkp-card-${item.id}`}
      onClick={() => onOpen(item)}
      className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-[#0a3d62]/40 hover:shadow-md"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-md bg-[#0a3d62]/8 px-2 py-0.5 text-[11px] font-semibold text-[#0a3d62]">{item.ket}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${FAS_STYLE[item.fasilitas]}`}>{item.fasilitas}</span>
      </div>
      <p className="mt-3 line-clamp-3 text-sm font-medium leading-relaxed text-slate-800">
        <Highlight text={item.nama} tokens={tokens} />
      </p>
      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3">
        <Check on={item.impor} label="Impor/Pemanfaatan" />
        <Check on={item.penyerahan} label="Penyerahan" />
        <span className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-semibold text-slate-500 group-hover:text-[#0a3d62]">
          {item.skb || "—"} <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </motion.button>
  );
}

function DetailRow({ label, children, id }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[170px_1fr] sm:gap-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="whitespace-pre-line text-sm leading-relaxed text-slate-800" data-testid={id}>{children || "—"}</div>
    </div>
  );
}

function DetailModal({ item, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto rounded-2xl p-0 [&>button]:text-white [&>button]:opacity-90" data-testid="bkp-detail-modal">
        {item && (
          <>
            <DialogHeader className="space-y-2 bg-[#0a3d62] px-6 py-5 text-left text-white">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                <span className="rounded-md bg-white/15 px-2 py-0.5">{item.bab}</span>
                <span className="rounded-md bg-white/15 px-2 py-0.5">{item.ket}</span>
              </div>
              <DialogTitle className="text-base font-bold leading-snug sm:text-lg" data-testid="bkp-detail-fasilitas">
                PPN {item.fasilitas}
              </DialogTitle>
              <DialogDescription className="text-xs text-sky-100/90">Sumber: Daftar Fasilitas PPN berdasarkan PP 49 Tahun 2022</DialogDescription>
            </DialogHeader>
            <div className="divide-y divide-slate-100 px-6 py-2">
              <DetailRow label="Ket." id="bkp-detail-ket">{item.ket}</DetailRow>
              <DetailRow label="Nama BKP/JKP" id="bkp-detail-nama">{item.nama}</DetailRow>
              <DetailRow label="Impor/Pemanfaatan" id="bkp-detail-impor"><Check on={item.impor} label={item.impor ? "Ya" : "Tidak"} /></DetailRow>
              <DetailRow label="Penyerahan" id="bkp-detail-penyerahan"><Check on={item.penyerahan} label={item.penyerahan ? "Ya" : "Tidak"} /></DetailRow>
              <DetailRow label="Fasilitas" id="bkp-detail-fasilitas-value">{item.fasilitas}</DetailRow>
              <DetailRow label="SKB/SKTD/tanpa" id="bkp-detail-skb">{item.skb}</DetailRow>
              <DetailRow label="Pengajuan Kemana" id="bkp-detail-pengajuan">{item.pengajuan}</DetailRow>
              <DetailRow label="Pihak yg Mengajukan" id="bkp-detail-pihak">{item.pihak}</DetailRow>
              <DetailRow label="Aturan Terkait" id="bkp-detail-aturan">{item.aturan}</DetailRow>
              <DetailRow label="Keterangan" id="bkp-detail-keterangan">{item.keterangan}</DetailRow>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function BkpJkp() {
  const [query, setQuery] = useState("");
  const [ket, setKet] = useState("");
  const [fas, setFas] = useState("");
  const [selected, setSelected] = useState(null);

  const tokens = useMemo(() => norm(query).split(" ").filter(Boolean), [query]);
  const results = useMemo(() => {
    const list = INDEX.filter((d) => (!ket || d.ket === ket) && (!fas || d.fasilitas === fas));
    if (!tokens.length) return list;
    return list
      .map((d) => ({ d, s: scoreItem(d, tokens) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s || a.d.id - b.d.id)
      .map((x) => x.d);
  }, [tokens, ket, fas]);

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="bkp-section">
        <div className="mb-2 flex items-center gap-2">
          <PackageSearch className="h-5 w-5 text-[#0a3d62]" />
          <h2 className="text-lg font-bold text-slate-800">Cari Fasilitas PPN per Jenis BKP/JKP</h2>
        </div>
        <p className="mb-5 flex items-center gap-1.5 text-sm text-slate-500">
          <ScrollText className="h-4 w-4" /> Berdasarkan PP 49 Tahun 2022 — {BKP_JKP_DATA.length} jenis BKP/JKP
        </p>

        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="bkp-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ketik nama barang/jasa, mis. buku, listrik, kapal, jasa pendidikan…"
            className="w-full rounded-xl border border-slate-200 bg-slate-50/60 py-3.5 pl-12 pr-11 text-sm font-medium text-slate-800 outline-none transition-colors focus:border-[#0a3d62] focus:bg-white focus:ring-2 focus:ring-[#0a3d62]/20"
          />
          {query && (
            <button data-testid="bkp-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="bkp-filter-ket">
          <Chip id="bkp-filter-ket-semua" active={!ket} onClick={() => setKet("")}>Semua</Chip>
          {KET_OPTIONS.map((k) => (
            <Chip key={k} id={`bkp-filter-ket-${slug(k)}`} active={ket === k} onClick={() => setKet(ket === k ? "" : k)}>{k}</Chip>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="bkp-filter-fasilitas">
          <Chip id="bkp-filter-fas-semua" active={!fas} onClick={() => setFas("")}>Semua Fasilitas</Chip>
          {FAS_OPTIONS.map((f) => (
            <Chip key={f} id={`bkp-filter-fas-${slug(f)}`} active={fas === f} onClick={() => setFas(fas === f ? "" : f)}>{f}</Chip>
          ))}
        </div>
      </section>

      <div className="mt-6 mb-3 text-sm text-slate-500" data-testid="bkp-result-count">
        Menampilkan <span className="font-semibold text-slate-800">{results.length}</span> jenis BKP/JKP yang mendapat fasilitas PPN
      </div>

      {results.length ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="bkp-results">
          {results.map((item, i) => (
            <ItemCard key={item.id} item={item} tokens={tokens} onOpen={setSelected} index={i} />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center" data-testid="bkp-empty-state">
          <PackageSearch className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-500">Tidak ditemukan BKP/JKP dengan kata kunci tersebut.</p>
          <p className="mt-1 text-xs text-slate-400">Coba kata kunci lain atau gunakan menu Tanya AI.</p>
        </div>
      )}

      <DetailModal item={selected} onClose={() => setSelected(null)} />
    </>
  );
}
